/**
 * WorkflowPanel — Enterprise Approval Workflow UI.
 *
 * Displays the current user's notice drafts and the available workflow actions
 * based on each draft's status and the user's role (owner = reviewer/approver,
 * member = drafter).
 *
 * Lifecycle: Draft -> Review -> Approved -> Published.
 * The existing Publish / Version History / Compare / Rollback UI lives in
 * ConsentNoticeDesigner and is NOT modified here — this panel is additive.
 */
import { useState } from 'react';
import type {
  NoticeDraftDetail,
  NoticeDraftStatus,
  NoticeDraftSummary,
  ReviewComment,
} from '../../lib/types';
import { Spinner } from '../../components/Spinner';
import { useToast } from '../../components/Toast';
import {
  addReviewComment,
  approveDraft,
  getDraft,
  getNetworkErrorMessage,
  publishDraft,
  rejectDraft,
  returnDraft,
  reviseDraft,
  submitDraft,
} from '../../lib/api';

interface WorkflowPanelProps {
  drafts: NoticeDraftSummary[];
  draftsLoading: boolean;
  draftsError: string | null;
  /** 'owner' = reviewer/approver, 'member' = drafter. */
  userRole: 'owner' | 'member';
  /** Called after any successful workflow transition so the parent refreshes. */
  onWorkflowChanged: () => Promise<void>;
}

const STATUS_STYLES: Record<NoticeDraftStatus, string> = {
  draft: 'border border-slate-700/30 bg-slate-800/30 text-slate-300',
  review: 'border border-amber-500/20 bg-amber-500/10 text-amber-400',
  approved: 'border border-blue-500/20 bg-blue-500/10 text-blue-400',
  rejected: 'border border-red-500/20 bg-red-500/10 text-red-400',
  published: 'border border-emerald-500/20 bg-emerald-500/10 text-emerald-400',
};

const STATUS_LABELS: Record<NoticeDraftStatus, string> = {
  draft: 'Draft',
  review: 'In Review',
  approved: 'Approved',
  rejected: 'Rejected',
  published: 'Published',
};

export function WorkflowPanel({
  drafts,
  draftsLoading,
  draftsError,
  userRole,
  onWorkflowChanged,
}: WorkflowPanelProps) {
  const { addToast } = useToast();
  const [activeDraftId, setActiveDraftId] = useState<string | null>(null);
  const [detail, setDetail] = useState<NoticeDraftDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [commentText, setCommentText] = useState('');

  const activeDraftSummary = drafts.find((d) => d.id === activeDraftId) ?? null;
  const isOwner = userRole === 'owner';

  const openDraft = async (draft: NoticeDraftSummary) => {
    setActiveDraftId(draft.id);
    setDetail(null);
    setDetailLoading(true);
    try {
      const fetched = await getDraft(draft.id);
      setDetail(fetched);
    } catch (e) {
      addToast({ type: 'error', message: getNetworkErrorMessage(e, 'Failed to load draft.') });
      setActiveDraftId(null);
    } finally {
      setDetailLoading(false);
    }
  };

  const closeDraft = () => {
    setActiveDraftId(null);
    setDetail(null);
    setCommentText('');
  };

  // Wrap every workflow action: show toast + refresh on success, toast on error.
  const runAction = async (
    label: string,
    fn: () => Promise<{ status: string }>,
    { successType = 'success' as 'success' | 'warning', successMsg }: { successType?: 'success' | 'warning'; successMsg: string },
  ) => {
    setActionLoading(true);
    try {
      const result = await fn();
      addToast({ type: successType, message: successMsg });
      await onWorkflowChanged();
      // Refresh the open detail view if it's the same draft.
      if (activeDraftId) {
        const refreshed = await getDraft(activeDraftId);
        setDetail(refreshed);
      }
      return result;
    } catch (e) {
      addToast({ type: 'error', message: getNetworkErrorMessage(e, `${label} failed.`) });
      throw e;
    } finally {
      setActionLoading(false);
    }
  };

  const handleSubmit = (draft: NoticeDraftSummary) =>
    runAction('Submit', () => submitDraft(draft.id, commentText || undefined), {
      successMsg: 'Draft submitted for review.',
    }).catch(() => {});

  const handleApprove = (draft: NoticeDraftSummary) =>
    runAction('Approve', () => approveDraft(draft.id, commentText || undefined), {
      successMsg: 'Draft approved. It can now be published.',
    }).then(async () => {
      setCommentText('');
    })
    .catch(() => {});

  const handleReject = (draft: NoticeDraftSummary) => {
    if (!commentText.trim()) {
      addToast({ type: 'warning', message: 'A comment is required to reject a draft.' });
      return;
    }
    runAction('Reject', () => rejectDraft(draft.id, commentText), {
      successType: 'warning',
      successMsg: 'Draft rejected and returned to the author.',
    })
      .then(async () => {
        setCommentText('');
      })
      .catch(() => {});
  };

  const handleReturn = (draft: NoticeDraftSummary) => {
    if (!commentText.trim()) {
      addToast({ type: 'warning', message: 'A comment is required to return a draft.' });
      return;
    }
    runAction('Return', () => returnDraft(draft.id, commentText), {
      successType: 'warning',
      successMsg: 'Draft returned for revision.',
    })
      .then(async () => {
        setCommentText('');
      })
      .catch(() => {});
  };

  const handleRevise = (draft: NoticeDraftSummary) =>
    runAction('Revise', () => reviseDraft(draft.id), {
      successMsg: 'Draft is back in edit mode.',
    }).catch(() => {});

  const handlePublish = (draft: NoticeDraftSummary) =>
    runAction('Publish', () => publishDraft(draft.id), {
      successMsg: 'Draft published successfully.',
    }).catch(() => {});

  const handleAddComment = async () => {
    if (!activeDraftId || !commentText.trim()) return;
    setActionLoading(true);
    try {
      await addReviewComment(activeDraftId, commentText.trim());
      addToast({ type: 'success', message: 'Comment added.' });
      setCommentText('');
      if (activeDraftId) {
        const refreshed = await getDraft(activeDraftId);
        setDetail(refreshed);
      }
    } catch (e) {
      addToast({ type: 'error', message: getNetworkErrorMessage(e, 'Failed to add comment.') });
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <div className="rounded-2xl border border-slate-900 bg-slate-900/20 p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-bold text-white flex items-center gap-2">
          <span className="h-2 w-2 rounded-full bg-indigo-400"></span>
          Approval Workflow
        </h3>
        <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">
          Your role: <span className={isOwner ? 'text-indigo-400' : 'text-slate-300'}>{userRole}</span>
        </span>
      </div>

      {/* Loading */}
      {draftsLoading && (
        <div className="flex items-center gap-3 py-8 justify-center">
          <Spinner size="sm" />
          <span className="text-xs text-slate-400">Loading drafts…</span>
        </div>
      )}

      {/* Error */}
      {draftsError && !draftsLoading && (
        <div className="rounded-xl border border-red-800/50 bg-red-950/20 p-4 text-xs text-red-300">
          <p>{draftsError}</p>
        </div>
      )}

      {/* Empty */}
      {!draftsLoading && !draftsError && drafts.length === 0 && (
        <div className="flex flex-col items-center justify-center py-8 text-center">
          <div className="text-3xl text-slate-500 mb-2">📝</div>
          <p className="text-sm text-slate-400">No drafts in the workflow.</p>
          <p className="text-xs text-slate-500 mt-1">
            Create a draft to start the approval process.
          </p>
        </div>
      )}

      {/* Drafts list */}
      {!draftsLoading && !draftsError && drafts.length > 0 && (
        <div className="space-y-2">
          {drafts.map((draft) => (
            <div
              key={draft.id}
              className={`flex flex-col sm:flex-row sm:items-center justify-between gap-3 rounded-xl border p-3 transition ${
                activeDraftId === draft.id
                  ? 'border-indigo-500/40 bg-indigo-500/5'
                  : 'border-slate-800 bg-slate-950/40 hover:border-slate-700'
              }`}
            >
              <div className="flex items-center gap-3">
                <span
                  className={`inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[10px] font-bold ${STATUS_STYLES[draft.status]}`}
                >
                  {STATUS_LABELS[draft.status]}
                </span>
                <div>
                  <p className="text-xs text-slate-300">
                    by <span className="font-semibold">{draft.authorEmail}</span>
                  </p>
                  {draft.version !== null && (
                    <p className="text-[10px] text-slate-500">Published as v{draft.version}</p>
                  )}
                  {draft.reviewerEmail && (
                    <p className="text-[10px] text-slate-500">
                      reviewed by {draft.reviewerEmail}
                    </p>
                  )}
                </div>
              </div>
              <button
                type="button"
                onClick={() =>
                  activeDraftId === draft.id ? closeDraft() : openDraft(draft)
                }
                className="self-start sm:self-auto rounded-lg border border-slate-800 bg-slate-900/40 px-3 py-1.5 text-[11px] font-semibold text-slate-300 hover:text-white hover:border-slate-700 transition"
              >
                {activeDraftId === draft.id ? 'Close' : 'View'}
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Expanded draft detail + workflow actions */}
      {activeDraftId && (
        <div className="mt-4 rounded-xl border border-slate-800 bg-slate-950/60 p-4 space-y-4">
          {detailLoading && (
            <div className="flex items-center gap-3 py-6 justify-center">
              <Spinner size="sm" />
              <span className="text-xs text-slate-400">Loading draft…</span>
            </div>
          )}

          {!detailLoading && activeDraftSummary && detail && (
            <>
              {/* Workflow action bar (context-sensitive) */}
              <WorkflowActionBar
                status={activeDraftSummary.status}
                isOwner={isOwner}
                actionLoading={actionLoading}
                onSubmit={() => handleSubmit(activeDraftSummary)}
                onApprove={() => handleApprove(activeDraftSummary)}
                onReject={() => handleReject(activeDraftSummary)}
                onReturn={() => handleReturn(activeDraftSummary)}
                onRevise={() => handleRevise(activeDraftSummary)}
                onPublish={() => handlePublish(activeDraftSummary)}
              />

              {/* Comment input */}
              <div className="space-y-2">
                <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-500">
                  Comment{' '}
                  {(activeDraftSummary.status === 'review' && isOwner) ||
                  activeDraftSummary.status === 'rejected'
                    ? ''
                    : '(optional)'}
                  {(activeDraftSummary.status === 'review' &&
                    (isOwner
                      ? ' — required to reject / return'
                      : '')) || ''}
                </label>
                <textarea
                  value={commentText}
                  onChange={(e) => setCommentText(e.target.value)}
                  rows={2}
                  placeholder="Leave a note for the reviewer or author…"
                  className="w-full rounded-lg border border-slate-800 bg-slate-900 px-3 py-2 text-xs text-slate-200 placeholder-slate-600 focus:border-indigo-500 outline-none resize-y"
                />
                <button
                  type="button"
                  onClick={handleAddComment}
                  disabled={actionLoading || !commentText.trim()}
                  className="rounded-lg border border-slate-800 bg-slate-900/40 px-3 py-1.5 text-[11px] font-semibold text-slate-300 hover:text-white hover:border-slate-700 disabled:opacity-40 transition"
                >
                  Add Comment
                </button>
              </div>

              {/* Comments timeline */}
              {detail.comments.length > 0 && (
                <CommentTimeline comments={detail.comments} />
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Workflow action bar — shows only the buttons valid for the current status.
// ---------------------------------------------------------------------------

interface WorkflowActionBarProps {
  status: NoticeDraftStatus;
  isOwner: boolean;
  actionLoading: boolean;
  onSubmit: () => void;
  onApprove: () => void;
  onReject: () => void;
  onReturn: () => void;
  onRevise: () => void;
  onPublish: () => void;
}

function WorkflowActionBar({
  status,
  isOwner,
  actionLoading,
  onSubmit,
  onApprove,
  onReject,
  onReturn,
  onRevise,
  onPublish,
}: WorkflowActionBarProps) {
  const btn =
    'rounded-lg px-4 py-2 text-[11px] font-semibold text-white shadow disabled:opacity-50 transition flex items-center justify-center gap-1.5';
  const primary = `${btn} bg-gradient-to-r from-indigo-600 to-blue-600 hover:from-indigo-500 hover:to-blue-500`;
  const successBtn = `${btn} bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500`;
  const warnBtn = `${btn} bg-gradient-to-r from-amber-600 to-orange-600 hover:from-amber-500 hover:to-orange-500`;
  const dangerBtn = `${btn} bg-gradient-to-r from-red-600 to-rose-600 hover:from-red-500 hover:to-rose-500`;

  return (
    <div className="rounded-lg border border-slate-800 bg-slate-900/40 p-3">
      <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-2">
        Available actions
      </p>
      <div className="flex flex-wrap gap-2">
        {status === 'draft' && (
          <button type="button" onClick={onSubmit} disabled={actionLoading} className={primary}>
            {actionLoading ? <Spinner size="sm" /> : '📤'} Submit for Review
          </button>
        )}

        {status === 'review' && isOwner && (
          <>
            <button type="button" onClick={onApprove} disabled={actionLoading} className={successBtn}>
              {actionLoading ? <Spinner size="sm" /> : '✓'} Approve
            </button>
            <button type="button" onClick={onReject} disabled={actionLoading} className={dangerBtn}>
              ✕ Reject
            </button>
            <button type="button" onClick={onReturn} disabled={actionLoading} className={warnBtn}>
              ↩ Return to Draft
            </button>
          </>
        )}

        {status === 'review' && !isOwner && (
          <p className="text-xs text-slate-500 italic py-1">
            Awaiting owner review.
          </p>
        )}

        {status === 'approved' && isOwner && (
          <button type="button" onClick={onPublish} disabled={actionLoading} className={successBtn}>
            {actionLoading ? <Spinner size="sm" /> : '🚀'} Publish
          </button>
        )}

        {status === 'approved' && !isOwner && (
          <p className="text-xs text-slate-500 italic py-1">
            Approved — an owner can publish this.
          </p>
        )}

        {status === 'rejected' && (
          <button type="button" onClick={onRevise} disabled={actionLoading} className={primary}>
            ↻ Revise & Resubmit
          </button>
        )}

        {status === 'published' && (
          <p className="text-xs text-emerald-400 italic py-1">
            This draft has been published.
          </p>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Comment timeline — chronological.
// ---------------------------------------------------------------------------

function CommentTimeline({ comments }: { comments: ReviewComment[] }) {
  return (
    <div className="space-y-2">
      <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
        Comments ({comments.length})
      </p>
      <div className="space-y-2 max-h-48 overflow-y-auto pr-1">
        {comments.map((c) => (
          <div key={c.id} className="rounded-lg border border-slate-800 bg-slate-950/50 p-3">
            <div className="flex items-center justify-between mb-1">
              <span className="text-[11px] font-semibold text-slate-300">{c.authorEmail}</span>
              <span
                className={`inline-flex items-center rounded-full px-1.5 py-0.5 text-[9px] font-bold ${STATUS_STYLES[c.status]}`}
              >
                {STATUS_LABELS[c.status]}
              </span>
            </div>
            <p className="text-xs text-slate-400 leading-relaxed">{c.comment}</p>
            <p className="text-[10px] text-slate-600 mt-1">
              {new Date(c.createdAt * 1000).toLocaleString()}
            </p>
          </div>
        ))}
      </div>
    </div>
  );
}
